/** Lo que el sistema configura en el `<script>` del widget (REQUIREMENTS 6.4). */
export interface WidgetConfig {
  /** Origen de la API. Por defecto, el mismo del que se cargó `widget.js`. */
  apiUrl: string;
  /**
   * `data-token-url`: endpoint del sistema que devuelve `{ token }` con el token de sesión del usuario
   * (REQUIREMENTS 6.4). Relativo a la página, ej. `/api/aura/token`. Si está, se usa en lugar de
   * la clave.
   */
  tokenUrl?: string;
  /**
   * `data-token-provider`: nombre de una función de la página (ej. `getLauraToken` o
   * `Lupa.lauraToken`) que devuelve el token de sesión del usuario, o `null` sin sesión. Para sistemas que
   * guardan la sesión en el navegador (Bearer) y no en una cookie. Tiene prioridad sobre
   * `data-token-url`.
   */
  tokenProvider?: string;
  /**
   * `data-tenant`: el sistema en Laura (su slug, ej. `lupa`). Va en cada pedido con el token: con eso
   * Laura sabe dónde validar la sesión. Obligatorio con `data-token-url` o `data-token-provider`.
   */
  tenant?: string;
  /** `data-access-key`: la clave compartida de la página de chat (sin sesión de un sistema). */
  accessKey?: string;
  /** `data-primary-color`: color principal (botón, mensajes del usuario). */
  primaryColor?: string;
  /** `data-title`: el subtítulo, debajo del nombre de la asistente. Por defecto, "Asistente de <sistema>". */
  title?: string;
  /**
   * `data-mode`: `floating` (por defecto: botón flotante en el sistema) o `page` (el chat ocupa la
   * página entera y pide la clave si hace falta: la página de chat de la API en `/`).
   */
  mode: 'floating' | 'page';
  /**
   * `data-show-sources="true"`: muestra las fuentes debajo de cada respuesta y las citas en el
   * texto. Lo decide cada sistema; sin el atributo no se muestran (la API las guarda igual).
   */
  showSources: boolean;
}

/** El slug de un tenant (como lo valida la API). */
const TENANT_SLUG = /^[a-z0-9][a-z0-9-]{1,62}$/;

/** Un nombre de función de la página: `getLauraToken` o `Lupa.lauraToken`. */
const FUNCTION_NAME = /^[A-Za-z_$][\w$]*(\.[A-Za-z_$][\w$]*)*$/;

/** Solo colores CSS simples: el valor termina en una propiedad de estilo. */
const COLOR = /^(#[0-9a-f]{3,8}|[a-z]+|rgba?\([\d\s.,%/]+\)|hsla?\([\d\s.,%/deg]+\))$/i;

export function readConfig(script: HTMLScriptElement | null): WidgetConfig | null {
  const data = script?.dataset ?? {};
  let apiUrl = data.apiUrl;
  if (!apiUrl && script?.src) apiUrl = new URL(script.src, document.baseURI).origin;
  if (!apiUrl) return null;
  const color = data.primaryColor?.trim();
  const provider = data.tokenProvider?.trim();
  const tenant = data.tenant?.trim();
  return {
    apiUrl: apiUrl.replace(/\/$/, ''),
    tokenUrl: data.tokenUrl ? new URL(data.tokenUrl, document.baseURI).href : undefined,
    tokenProvider: provider && FUNCTION_NAME.test(provider) ? provider : undefined,
    tenant: tenant && TENANT_SLUG.test(tenant) ? tenant : undefined,
    accessKey: data.accessKey || undefined,
    primaryColor: color && COLOR.test(color) ? color : undefined,
    title: data.title?.trim() || undefined,
    mode: data.mode === 'page' ? 'page' : 'floating',
    showSources: data.showSources?.trim().toLowerCase() === 'true',
  };
}
